import React from "react";
import { useRecoilValue } from "recoil";
import { OBJECTIVE_PRESETS } from "shared/domain/objectivePresets";
import { Study } from "features/optimization/state/studyAtoms";
import { errorCodesState } from "features/error-codes/state/errorCodesAtoms";
import { lookupErrorMessage } from "features/error-codes/utils/lookupErrorMessage";

export interface DataPoint {
  trial: number;
  cfd_id_1: string | null;
  value_1: string | null;
  min_1: string | null;
  cfd_id_2: string | null;
  value_2: string | null;
  min_2: string | null;
  // 多目的でも trial 単位で 1 個（API 契約: error_code_list_1 のみ）
  error_code: string | null;
  // 失敗 trial の × マーカー描画用 Y 値（current min と同じ位置に置く）
  // value が null かつ error_code 非 null のときのみ非 null
  failed_value_1: number | null;
}

// 失敗 trial の Y 位置を「左隣の min → 右隣の value」優先で解決する。
// どちらも存在しない（study 内に成功 trial 皆無）場合は null を返し、
// 呼び出し側はバッジ表示等の代替 UI を提供する想定。
const resolveFailedY = (
  index: number,
  values: (number | null)[],
  mins: (number | null)[],
): number | null => {
  for (let j = index - 1; j >= 0; j--) {
    if (mins[j] !== null) return mins[j];
  }
  for (let j = index + 1; j < values.length; j++) {
    if (values[j] !== null) return values[j];
  }
  return null;
};

export const createDataList = (study: Study | null): DataPoint[] => {
  if (!study) return [];

  const preset = OBJECTIVE_PRESETS[study.presetId];
  const secondaryKey = preset.objectives[1]?.key;

  const obj_lst_1 = study.objective_list?.objective_list_1 ?? [];
  const obj_lst_2 = study.objective_list?.objective_list_2 ?? [];
  const cfd_id_1: string[] = study.cfd_id_list?.cfd_id_list_1 ?? [];
  const cfd_id_2: string[] = study.cfd_id_list?.cfd_id_list_2 ?? [];
  const error_codes: (string | null)[] = study.error_code_list?.error_code_list_1 ?? [];

  const isSingle = !secondaryKey || obj_lst_2.length === 0;

  // 単目的の場合
  if (isSingle) {
    if (obj_lst_1.length === 0) return [];

    // 1 パス目: value / min を計算
    const numericValues: (number | null)[] = [];
    const numericMins: (number | null)[] = [];
    let currentMin: number | null = null;
    for (let i = 0; i < obj_lst_1.length; i++) {
      const raw = obj_lst_1[i];
      if (raw !== null) {
        const numValue = Number(raw);
        currentMin = currentMin !== null ? Math.min(currentMin, numValue) : numValue;
        numericValues.push(numValue);
        numericMins.push(currentMin);
      } else {
        numericValues.push(null);
        numericMins.push(currentMin);
      }
    }

    // 2 パス目: 失敗 trial に対し左隣→右隣で Y 位置を解決
    const dataList: DataPoint[] = [];
    for (let i = 0; i < obj_lst_1.length; i++) {
      const code = error_codes[i] ?? null;
      const numValue = numericValues[i];
      const currentMinAtI = numericMins[i];
      const isFailed = numValue === null && code !== null;
      dataList.push({
        trial: i + 1,
        cfd_id_1: cfd_id_1[i] ?? null,
        value_1: numValue !== null ? numValue.toFixed(3) : null,
        min_1: currentMinAtI !== null ? currentMinAtI.toFixed(3) : null,
        cfd_id_2: null,
        value_2: null,
        min_2: null,
        error_code: code,
        failed_value_1: isFailed ? resolveFailedY(i, numericValues, numericMins) : null,
      });
    }
    return dataList;
  }
  // 多目的の場合
  if (obj_lst_1.length === 0 || obj_lst_2.length === 0) return [];

  const length = Math.max(obj_lst_1.length, obj_lst_2.length);

  // 1 パス目: value_1 / min_1 / value_2 / min_2 を計算
  const numericValues1: (number | null)[] = [];
  const numericMins1: (number | null)[] = [];
  const numericValues2: (number | null)[] = [];
  const numericMins2: (number | null)[] = [];
  let currentMin1: number | null = null;
  let currentMin2: number | null = null;
  for (let i = 0; i < length; i++) {
    const raw1 = obj_lst_1[i];
    if (raw1 !== null && raw1 !== undefined) {
      const val = Number(raw1);
      if (Number.isFinite(val)) {
        currentMin1 = currentMin1 !== null ? Math.min(currentMin1, val) : val;
        numericValues1.push(val);
      } else {
        numericValues1.push(null);
      }
    } else {
      numericValues1.push(null);
    }
    numericMins1.push(currentMin1);

    const raw2 = obj_lst_2[i];
    if (raw2 !== null && raw2 !== undefined) {
      const val = Number(raw2);
      if (Number.isFinite(val)) {
        currentMin2 = currentMin2 !== null ? Math.min(currentMin2, val) : val;
        numericValues2.push(val);
      } else {
        numericValues2.push(null);
      }
    } else {
      numericValues2.push(null);
    }
    numericMins2.push(currentMin2);
  }

  // 2 パス目
  const dataList: DataPoint[] = [];
  for (let i = 0; i < length; i++) {
    const code = error_codes[i] ?? null;
    const v1 = numericValues1[i];
    const m1 = numericMins1[i];
    const v2 = numericValues2[i];
    const m2 = numericMins2[i];
    const isFailed = v1 === null && code !== null;
    dataList.push({
      trial: i + 1,
      cfd_id_1: cfd_id_1?.[i] ?? null,
      value_1: v1 !== null ? v1.toFixed(3) : null,
      min_1: m1 !== null ? m1.toFixed(3) : null,
      cfd_id_2: cfd_id_2?.[i] ?? null,
      value_2: v2 !== null ? v2.toFixed(3) : null,
      min_2: m2 !== null ? m2.toFixed(3) : null,
      error_code: code,
      failed_value_1: isFailed ? resolveFailedY(i, numericValues1, numericMins1) : null,
    });
  }

  return dataList;
};

// 全 trial 失敗（study 内に成功 trial が一つも無く、かつ少なくとも 1 件 error_code 付き）
// のときのみ、バッジ表示用の集計を返す。それ以外は null。
// 多目的でも error_code は trial 単位で 1 つ（error_code_list_1 のみ）なので
// objective_list_1 の全 null 判定で十分。
export interface FailedOnlySummary {
  count: number;
  codes: Array<{ trial: number; code: string }>;
}

export const summarizeFailedOnly = (study: Study | null): FailedOnlySummary | null => {
  if (!study) return null;
  const objs = study.objective_list?.objective_list_1 ?? [];
  if (objs.length === 0) return null;
  const codes = study.error_code_list?.error_code_list_1 ?? [];
  const hasAnySuccess = objs.some(v => v !== null);
  if (hasAnySuccess) return null;
  const failed: Array<{ trial: number; code: string }> = [];
  objs.forEach((_, i) => {
    const c = codes[i] ?? null;
    if (c !== null) failed.push({ trial: i + 1, code: c });
  });
  if (failed.length === 0) return null;
  return { count: failed.length, codes: failed };
};

// Recharts <Tooltip content={...} /> 用カスタム描画コンポーネント。
// 既存のデフォルト Tooltip と同等の Trial 番号・系列値・色付きラベルを再現しつつ、
// 失敗 trial の場合は日本語のエラー理由を独立行で追加する。
interface RechartsTooltipPayloadItem {
  name?: string;
  value?: number | string | null;
  color?: string;
  dataKey?: string | number;
  payload?: DataPoint;
}

interface CustomTooltipContentProps {
  active?: boolean;
  label?: number | string;
  payload?: RechartsTooltipPayloadItem[];
}

const tooltipBoxStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(0,0,0,0.12)',
  padding: '8px 12px',
  borderRadius: 4,
  fontSize: 13,
  lineHeight: 1.5,
  color: '#222',
};

export const CustomTooltipContent: React.FC<CustomTooltipContentProps> = ({
  active,
  label,
  payload,
}) => {
  const errorCodesDict = useRecoilValue(errorCodesState);

  if (!active || !payload || payload.length === 0) return null;

  // payload[0].payload に DataPoint 全体が乗る（同 trial なので 1 つ取れば十分）
  const dataPoint = payload[0]?.payload;
  const lookup = lookupErrorMessage(dataPoint?.error_code ?? null, errorCodesDict);
  const errorText = lookup
    ? lookup.message ?? `不明なエラー (${lookup.code})`
    : null;

  return (
    <div style={tooltipBoxStyle}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{`Trial ${label}`}</div>
      {payload.map((item, idx) => (
        <div key={`${item.dataKey ?? idx}`} style={{ color: item.color ?? '#222' }}>
          {item.name}: {item.value ?? 'N/A'}
        </div>
      ))}
      {errorText && (
        <div style={{ marginTop: 6, color: '#c0392b' }}>
          <span style={{ fontWeight: 600 }}>失敗理由: </span>
          <span>{errorText}</span>
        </div>
      )}
    </div>
  );
};

// Hypervolume チャート用のデータポイント
export interface HypervolumeDataPoint {
  trial: number;
  hypervolume: number | null;
}

// Hypervolume データリスト生成（多目的最適化時のみ使用）
export const createHypervolumeDataList = (study: Study | null): HypervolumeDataPoint[] => {
  if (!study || !study.hypervolume_list) return [];

  return study.hypervolume_list.map((hv, index) => ({
    trial: index + 1,
    hypervolume: hv,
  }));
};
